/**
 * SoldierTemplatePicker component for selecting a soldier template.
 * Shows all official templates with their fixed stats and costs.
 * Disables templates that can't be afforded.
 */

import { Badge } from '@/components/ui';
import { Shield, Swords, Target, Footprints, Brain, Heart, Coins } from 'lucide-react';
import { cn } from '@/utils';
import {
  SOLDIER_TEMPLATES,
  getAllTemplateIds,
  type SoldierTemplateId,
  type SoldierTemplate,
} from '@/store/warbandSlice';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SoldierTemplatePickerProps {
  onSelect: (templateId: SoldierTemplateId) => void;
  canAddSpecialist: boolean;
  availableGold: number;
}

// ---------------------------------------------------------------------------
// Template Card Component
// ---------------------------------------------------------------------------

interface TemplateCardProps {
  template: SoldierTemplate;
  onSelect: () => void;
  disabled: boolean;
  disabledReason?: string;
  canAfford: boolean;
}

function TemplateCard({ template, onSelect, disabled, disabledReason, canAfford }: TemplateCardProps) {
  const isDisabled = disabled || !canAfford;

  return (
    <button
      onClick={onSelect}
      disabled={isDisabled}
      className={cn(
        'w-full rounded-lg border border-border bg-card p-3 text-left',
        'transition-colors hover:bg-accent',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        !canAfford && 'border-destructive/50'
      )}
    >
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-medium">{template.name}</span>
          {template.isSpecialist ? (
            <Badge variant="secondary" className="text-[10px]">
              Specialist
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px]">
              Standard
            </Badge>
          )}
        </div>
        <div className={cn(
          'flex items-center gap-1 text-sm font-semibold',
          !canAfford && 'text-destructive'
        )}>
          <Coins className="h-3 w-3" />
          {template.cost}gc
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-6 gap-2 text-center text-xs">
        <div className="flex flex-col items-center">
          <Footprints className="mb-0.5 h-3 w-3 text-muted-foreground" />
          <span className="font-mono">{template.move}</span>
        </div>
        <div className="flex flex-col items-center">
          <Swords className="mb-0.5 h-3 w-3 text-muted-foreground" />
          <span className="font-mono">
            {template.fight > 0 ? `+${template.fight}` : template.fight}
          </span>
        </div>
        <div className="flex flex-col items-center">
          <Target className="mb-0.5 h-3 w-3 text-muted-foreground" />
          <span className="font-mono">
            {template.shoot > 0 ? `+${template.shoot}` : template.shoot}
          </span>
        </div>
        <div className="flex flex-col items-center">
          <Shield className="mb-0.5 h-3 w-3 text-muted-foreground" />
          <span className="font-mono">{template.armor}</span>
        </div>
        <div className="flex flex-col items-center">
          <Brain className="mb-0.5 h-3 w-3 text-muted-foreground" />
          <span className="font-mono">
            {template.will > 0 ? `+${template.will}` : template.will}
          </span>
        </div>
        <div className="flex flex-col items-center">
          <Heart className="mb-0.5 h-3 w-3 text-muted-foreground" />
          <span className="font-mono">{template.health}</span>
        </div>
      </div>

      {/* Disabled reason */}
      {isDisabled && (
        <p className="mt-2 text-[10px] text-destructive">
          {!canAfford ? 'Not enough gold' : disabledReason || 'Specialist cap reached'}
        </p>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function SoldierTemplatePicker({
  onSelect,
  canAddSpecialist,
  availableGold,
}: SoldierTemplatePickerProps) {
  const templateIds = getAllTemplateIds();

  // Group templates by type
  const standardTemplates = templateIds
    .filter((id) => !SOLDIER_TEMPLATES[id].isSpecialist)
    .map((id) => SOLDIER_TEMPLATES[id]);

  const specialistTemplates = templateIds
    .filter((id) => SOLDIER_TEMPLATES[id].isSpecialist)
    .map((id) => SOLDIER_TEMPLATES[id]);

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Footprints className="h-3 w-3" /> Move
        </span>
        <span className="flex items-center gap-1">
          <Swords className="h-3 w-3" /> Fight
        </span>
        <span className="flex items-center gap-1">
          <Target className="h-3 w-3" /> Shoot
        </span>
        <span className="flex items-center gap-1">
          <Shield className="h-3 w-3" /> Armor
        </span>
        <span className="flex items-center gap-1">
          <Brain className="h-3 w-3" /> Will
        </span>
        <span className="flex items-center gap-1">
          <Heart className="h-3 w-3" /> Health
        </span>
      </div>

      {/* Standard Soldiers */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
          Standard Soldiers (No Cap)
        </h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {standardTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onSelect={() => onSelect(template.id)}
              disabled={false}
              canAfford={availableGold >= template.cost}
            />
          ))}
        </div>
      </div>

      {/* Specialists */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
          Specialists (Max 4)
        </h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {specialistTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onSelect={() => onSelect(template.id)}
              disabled={!canAddSpecialist}
              disabledReason="Specialist cap reached"
              canAfford={availableGold >= template.cost}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
